import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { fetchEvent, fetchEventActivity } from '../services/eventService';
import { downloadEventExcel, downloadEventReport, downloadPollExcel, downloadPollReport, pollResponseLabel, rsvpCell, rsvpGuestRows, summarizeResponses, collectCustomFieldColumns, customAnswerValue, rsvpColumnConfig } from '../utils/eventReportExport';
import { isPollEvent } from '../utils/pollUtils';

const STATUS_BADGE = {
  ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  DECLINED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  TENTATIVE: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  INVITED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};

const rowValues = (item, customColumns) => {
  const { p, cell, guests, row, index } = item;
  const guest = guests[index] || {};
  const values = {
    name: cell.name,
    status: cell.status,
    forwardedBy: p.forwardedBy || '',
    people: cell.guestCount || '',
    guest: row.guest || '',
    age: guest.age != null && guest.age !== '' ? String(guest.age) : '',
    food: row.food || '',
    drinks: row.drinks || '',
  };
  customColumns.forEach((col) => {
    values[`custom:${col.id}`] = customAnswerValue(p, col);
  });
  return values;
};

const compareValues = (left, right) => {
  const a = String(left || '').trim();
  const b = String(right || '').trim();
  const aNum = Number(a);
  const bNum = Number(b);
  if (a !== '' && b !== '' && !Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
};

const ColumnMenu = ({ column, open, filterValue, sort, onToggle, onSort, onFilter, onClose }) => {
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const active = String(filterValue || '').trim() || (sort?.id === column.id);
  const ascOn = sort?.id === column.id && sort.dir === 'asc';
  const descOn = sort?.id === column.id && sort.dir === 'desc';

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 2,
        left: Math.max(8, Math.min(rect.left - 8, window.innerWidth - 292)),
        width: 280,
        zIndex: 80,
      });
    };
    place();
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) onClose();
    };
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, onClose]);

  return (
    <div ref={rootRef} className="relative inline-flex items-center">
      <button
        ref={buttonRef}
        type="button"
        onClick={onToggle}
        className={`ml-1.5 inline-flex h-[18px] w-[18px] items-center justify-center rounded-[3px] border bg-white text-gray-500 shadow-sm ${
          open || active
            ? 'border-gray-300 text-gray-700'
            : 'border-gray-200 hover:border-gray-300 hover:text-gray-700'
        }`}
        aria-label={`Column options for ${column.label}`}
      >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && menuStyle && (
        <div
          style={menuStyle}
          className="overflow-hidden rounded-md border border-gray-200 bg-white text-gray-800 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
        >
          <div className="px-4 py-2.5 text-[13px] text-gray-500">Column options</div>
          <div className="border-t border-gray-200 px-4 py-2.5 text-[13px] text-gray-700">Filter data</div>
          <div className="px-3 pb-2">
            <input
              type="text"
              value={filterValue || ''}
              onChange={(e) => onFilter(e.target.value)}
              placeholder="Search"
              className="w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] font-normal text-gray-800 outline-none focus:border-emerald-500"
            />
          </div>
          <div className="flex items-stretch border-t border-gray-200">
            <div className="flex flex-1 items-center bg-emerald-500 px-4 py-2.5 text-[13px] font-medium text-white">
              Sort
            </div>
            <button
              type="button"
              onClick={() => onSort('asc')}
              className={`flex items-center gap-1 border-l border-emerald-600 px-3 py-2.5 text-[13px] font-medium ${
                ascOn ? 'bg-emerald-600 text-white' : 'bg-emerald-500 text-white hover:bg-emerald-600'
              }`}
            >
              <span aria-hidden="true">↑</span>
              A-Z
            </button>
            <button
              type="button"
              onClick={() => onSort('desc')}
              className={`flex items-center gap-1 border-l border-emerald-600 px-3 py-2.5 text-[13px] font-medium ${
                descOn ? 'bg-emerald-600 text-white' : 'bg-emerald-500 text-white hover:bg-emerald-600'
              }`}
            >
              <span aria-hidden="true">↓</span>
              Z-A
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PIE_SLICES = [
  { key: 'accepted', label: 'Accepted', color: '#059669' },
  { key: 'maybe', label: 'Maybe', color: '#0284c7' },
  { key: 'declined', label: 'Declined', color: '#e11d48' },
  { key: 'pending', label: 'Pending', color: '#d97706' },
];

const ResponsePie = ({ counts }) => {
  const slices = PIE_SLICES.map((slice) => ({ ...slice, value: counts[slice.key] || 0 })).filter((slice) => slice.value > 0);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <div className="flex items-center gap-4">
      <div className="h-28 w-28 flex-shrink-0">
        {total === 0 ? (
          <div className="h-full w-full rounded-full border border-dashed border-gray-300 dark:border-gray-600" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={slices} dataKey="value" nameKey="label" innerRadius={28} outerRadius={52} paddingAngle={2} stroke="none">
                {slices.map((slice) => (
                  <Cell key={slice.key} fill={slice.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      <ul className="space-y-1.5 text-xs text-gray-700 dark:text-gray-200">
        {PIE_SLICES.map((slice) => (
          <li key={slice.key} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: slice.color }} />
            <span>{slice.label}</span>
            <span className="font-semibold">{counts[slice.key] || 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const matchesFilter = (value, query) => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return String(value || '').toLowerCase().includes(needle);
};

const EventSenderAnalytics = ({ eventId, eventName, fallbackParticipants = [], rsvpFields = null, eventType = '' }) => {
  const [participants, setParticipants] = useState(fallbackParticipants);
  const [sourceEvent, setSourceEvent] = useState(null);
  const [fieldConfig, setFieldConfig] = useState(rsvpFields);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [openColumn, setOpenColumn] = useState(null);
  const [sort, setSort] = useState(null);
  const fallbackRef = React.useRef(fallbackParticipants);
  const fieldsRef = React.useRef(rsvpFields);
  fallbackRef.current = fallbackParticipants;
  fieldsRef.current = rsvpFields;

  const loadAnalytics = useCallback(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    setLoading(true);
    const fallbackList = fallbackRef.current || [];
    const incomingFields = fieldsRef.current;
    Promise.all([
      fetchEvent(eventId).catch(() => null),
      fetchEventActivity(eventId).catch(() => []),
    ]).then(([event, activity]) => {
      if (cancelled) return;
      if (event) setSourceEvent(event);
      const detailsById = new Map(
        fallbackList.map((p) => [String(p.userId), p]),
      );
      const source = Array.isArray(event?.participants) && event.participants.length
        ? event.participants
        : fallbackList;
      if (event?.responsePolicy?.rsvpFields) {
        setFieldConfig(event.responsePolicy.rsvpFields);
      } else if (incomingFields) {
        setFieldConfig(incomingFields);
      }
      const list = Array.isArray(activity) ? activity : [];
      const forwardedByUserId = new Map();
      const forwardedPeople = new Map();
      list
        .filter((item) => String(item.action || '').toUpperCase() === 'FORWARDED')
        .forEach((item) => {
          const by = item.actorName || item.metadata?.forwardedByName || '';
          const named = Array.isArray(item.metadata?.targetUsers) ? item.metadata.targetUsers : [];
          named.forEach((person) => {
            const id = String(person?.userId || '');
            if (!id) return;
            forwardedPeople.set(id, {
              userId: id,
              userName: person.userName || 'User',
              forwardedBy: by,
            });
            if (by) forwardedByUserId.set(id, by);
          });
          const ids = Array.isArray(item.metadata?.targetUserIds) ? item.metadata.targetUserIds : [];
          ids.forEach((id) => {
            if (!id) return;
            const key = String(id);
            if (by) {
              const existing = forwardedByUserId.get(key);
              forwardedByUserId.set(key, existing && existing !== by ? `${existing}, ${by}` : by);
            }
            if (!forwardedPeople.has(key)) {
              forwardedPeople.set(key, { userId: key, userName: 'User', forwardedBy: by });
            }
          });
        });
      const mapped = source.map((p) => {
        const fallback = detailsById.get(String(p.userId));
        const fetchedDetails = p.responseDetails || {};
        const fallbackDetails = fallback?.responseDetails || {};
        const fetchedGuests = fetchedDetails.guests;
        const fetchedCustom = fetchedDetails.customAnswers;
        const responseDetails = {
          ...fallbackDetails,
          ...fetchedDetails,
          guests: (Array.isArray(fetchedGuests) && fetchedGuests.length)
            ? fetchedGuests
            : (fallbackDetails.guests || fetchedGuests || []),
          customAnswers: (Array.isArray(fetchedCustom) && fetchedCustom.length)
            ? fetchedCustom
            : (fallbackDetails.customAnswers || fetchedCustom || []),
        };
        const known = forwardedPeople.get(String(p.userId));
        return {
          ...p,
          userName: (p.userName && p.userName !== 'User')
            ? p.userName
            : (fallback?.userName && fallback.userName !== 'User'
              ? fallback.userName
              : (known?.userName && known.userName !== 'User' ? known.userName : (p.userName || 'User'))),
          forwardedBy: p.forwardedByName || forwardedByUserId.get(String(p.userId)) || '',
          responseDetails,
        };
      });
      const present = new Set(mapped.map((p) => String(p.userId)));
      forwardedPeople.forEach((person) => {
        if (!person.userId || present.has(String(person.userId))) return;
        mapped.push({
          userId: person.userId,
          userName: person.userName || 'User',
          status: 'INVITED',
          role: 'OPTIONAL',
          forwardedBy: person.forwardedBy || '',
          responseDetails: {},
        });
      });
      setParticipants(mapped);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [eventId]);

  useEffect(() => loadAnalytics(), [loadAnalytics]);

  const isPoll = isPollEvent(sourceEvent || eventType);
  const pollRows = useMemo(
    () => (participants || []).map((p) => ({
      id: p.userId,
      name: p.userName || 'User',
      response: pollResponseLabel(p, sourceEvent),
    })),
    [participants, sourceEvent],
  );
  const pollVoted = pollRows.filter((row) => row.response !== 'No vote').length;
  const counts = useMemo(() => summarizeResponses(participants), [participants]);
  const statCards = [
    { label: 'Invited', value: counts.invited, tone: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100' },
    { label: 'Responded', value: counts.responded, tone: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200' },
    { label: 'Accepted', value: counts.accepted, tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
    { label: 'Maybe', value: counts.maybe, tone: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' },
    { label: 'Declined', value: counts.declined, tone: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
    { label: 'Pending', value: counts.pending, tone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  ];

  const columns = useMemo(() => rsvpColumnConfig(fieldConfig), [fieldConfig]);
  const customColumns = useMemo(
    () => collectCustomFieldColumns(participants, fieldConfig),
    [participants, fieldConfig],
  );
  const exportPayload = { eventName, participants, rsvpFields: fieldConfig };
  const perGuest = columns.guestNames || columns.guestAges || columns.food || columns.drinks;
  const filterColumns = [
    { id: 'name', label: 'Name' },
    { id: 'status', label: 'Status' },
    { id: 'forwardedBy', label: 'Forwarded by' },
    columns.guestCount ? { id: 'people', label: 'People' } : null,
    columns.guestNames ? { id: 'guest', label: 'Guest' } : null,
    columns.guestAges ? { id: 'age', label: 'Age' } : null,
    columns.food ? { id: 'food', label: 'Food' } : null,
    columns.drinks ? { id: 'drinks', label: 'Drinks' } : null,
    ...customColumns.map((col) => ({ id: `custom:${col.id}`, label: col.label })),
  ].filter(Boolean);
  const hasActiveFilter = filterColumns.some((col) => String(filters[col.id] || '').trim()) || Boolean(sort?.id);

  const visibleRows = useMemo(() => {
    const rows = participants.flatMap((p) => {
      const cell = rsvpCell(p);
      const status = (p.status || 'PENDING').toUpperCase();
      const guests = Array.isArray(p.responseDetails?.guests) ? p.responseDetails.guests : [];
      const sourceRows = perGuest ? rsvpGuestRows(p) : [{ person: cell.name, status: cell.status, guest: '', food: '', drinks: '' }];
      return sourceRows
        .map((row, index) => ({ p, cell, status, guests, row, index }))
        .filter((item) => {
          const values = rowValues(item, customColumns);
          return filterColumns.every((col) => matchesFilter(values[col.id], filters[col.id]));
        });
    });
    if (!sort?.id) return rows;
    return [...rows].sort((left, right) => {
      const result = compareValues(
        rowValues(left, customColumns)[sort.id],
        rowValues(right, customColumns)[sort.id],
      );
      return sort.dir === 'desc' ? -result : result;
    });
  }, [participants, perGuest, filters, filterColumns, customColumns, sort]);

  if (isPoll) {
    const pollExport = { eventName, participants, eventInfo: sourceEvent };
    return (
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-3 bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Analytics</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {pollVoted} of {pollRows.length} voted
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-1.5">
              <button
                type="button"
                onClick={loadAnalytics}
                disabled={loading}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={() => downloadPollExcel(pollExport)}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Download CSV
              </button>
              <button
                type="button"
                onClick={() => downloadPollReport(pollExport)}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800"
              >
                Download report
              </button>
            </div>
          </div>
        </div>
        <div className="p-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">Poll responses</p>
          <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-800">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-2.5 py-2 font-semibold whitespace-nowrap">Participant</th>
                  <th className="px-2.5 py-2 font-semibold whitespace-nowrap">Response</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {pollRows.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-2.5 py-3 text-gray-500">
                      {loading ? 'Refreshing…' : 'No participants yet'}
                    </td>
                  </tr>
                )}
                {pollRows.map((row) => (
                  <tr key={row.id || row.name} className="text-gray-800 dark:text-gray-200">
                    <td className="px-2.5 py-2 font-medium whitespace-nowrap">{row.name}</td>
                    <td className="px-2.5 py-2">{row.response}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-3 py-3 bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Analytics</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {counts.responded} of {counts.invited} responded
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-1.5">
            <button
              type="button"
              onClick={loadAnalytics}
              disabled={loading}
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={() => downloadEventExcel(exportPayload)}
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Download CSV
            </button>
            <button
              type="button"
              onClick={() => downloadEventReport(exportPayload)}
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800"
            >
              Download report
            </button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <ResponsePie counts={counts} />
          <div className="grid flex-1 grid-cols-3 sm:grid-cols-6 gap-1.5">
            {statCards.map((stat) => (
              <div key={stat.label} className={`rounded-xl px-2 py-2 ${stat.tone}`}>
                <p className="text-lg font-bold leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide mt-1 opacity-80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-3 space-y-4">
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">RSVP responses</p>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => {
                  setFilters({});
                  setSort(null);
                }}
                className="text-[11px] font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-800">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400">
                <tr>
                  {filterColumns.map((col) => (
                    <th key={col.id} className="px-2.5 py-2 font-semibold whitespace-nowrap">
                      <div className="inline-flex items-center">
                        <span>{col.label}</span>
                        <ColumnMenu
                          column={col}
                          open={openColumn === col.id}
                          filterValue={filters[col.id]}
                          sort={sort}
                          onToggle={() => setOpenColumn((current) => (current === col.id ? null : col.id))}
                          onClose={() => setOpenColumn(null)}
                          onSort={(dir) => {
                            setSort({ id: col.id, dir });
                            setOpenColumn(null);
                          }}
                          onFilter={(value) => setFilters((prev) => ({ ...prev, [col.id]: value }))}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={filterColumns.length} className="px-2.5 py-3 text-gray-500">
                      {loading ? 'Refreshing…' : (hasActiveFilter ? 'No rows match these filters' : 'No participants yet')}
                    </td>
                  </tr>
                )}
                {visibleRows.map(({ p, cell, status, guests, row, index }) => {
                  const first = index === 0;
                  return (
                  <tr key={`${p.userId || cell.name}-${index}`} className="text-gray-800 dark:text-gray-200">
                    <td className="px-2.5 py-2 font-medium whitespace-nowrap">{cell.name}</td>
                    <td className="px-2.5 py-2">
                      {first && (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[status] || STATUS_BADGE.PENDING}`}>
                          {cell.status}
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{first ? (p.forwardedBy || '—') : ''}</td>
                    {columns.guestCount && (
                      <td className="px-2.5 py-2 whitespace-nowrap">{first ? (cell.guestCount || '—') : ''}</td>
                    )}
                    {columns.guestNames && (
                      <td className="px-2.5 py-2 whitespace-nowrap">{row.guest || '—'}</td>
                    )}
                    {columns.guestAges && (
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        {guests[index]?.age != null && guests[index].age !== '' ? guests[index].age : '—'}
                      </td>
                    )}
                    {columns.food && (
                      <td className="px-2.5 py-2 whitespace-nowrap">{row.food || '—'}</td>
                    )}
                    {columns.drinks && (
                      <td className="px-2.5 py-2 whitespace-nowrap">{row.drinks || '—'}</td>
                    )}
                    {customColumns.map((col) => (
                      <td key={col.id} className="px-2.5 py-2 whitespace-nowrap">
                        {first ? (customAnswerValue(p, col) || '—') : ''}
                      </td>
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventSenderAnalytics;
